// ©2024 Austin App House. All rights reserved.
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import Loader from '../dashboard/common/Loader';
import Notification from '../notification';
import { toggleModal, toggleSecondModalClose } from '../../storage/actions/modalsActions';
import { dashboard as dashboardSelectStyle, dashboardSelectClassNames } from '../../utils/customSelectStyle';
import { fundsInputValueValidator, isValidFundsAmount } from '../../utils/validators';
import CustomSelect from '../layout/CustomSelect';
import AddBankAccount from './BankPayments/BankAccount';
import ConvergeCheckoutPayment from './ElavonPayments/ConvergeCheckoutPayment';
import { reloadData } from '../../storage/actions/applicationStateActions';
import {
  chargeSavedElavonCard,
  mapPaymentMethodsForSelect,
  PROVIDER_ELAVON,
} from '../../utils/elavonPayments';
import { chargeSavedBankAccount } from '../../utils/paymentBanks';

const FUND_TYPE_OPTIONS = [
  { label: 'Bank Account', value: 'bank' },
  { label: 'Credit Card', value: 'card' },
];

function grossAmount(net, processingFee, type) {
  const cardFeePercent = 0.0329;
  const fundsAmount = net + (net * processingFee);

  if (type === 'card') {
    const cardFeeAmount = net * cardFeePercent;
    return fundsAmount + cardFeeAmount;
  }

  return fundsAmount;
}

const DashboardSelect = ({
  inputId,
  options,
  value,
  onChange,
  isClearable,
  isSearchable,
  placeholder,
}) => (
  <div className="dashboard-select-wrap">
    <CustomSelect
      inputId={inputId}
      styles={dashboardSelectStyle}
      classNames={dashboardSelectClassNames}
      options={options}
      value={value}
      onChange={onChange}
      isClearable={isClearable}
      isSearchable={isSearchable}
      placeholder={placeholder}
    />
  </div>
);

const AddFunds = ({
  modalState, toggleModal, user,
  toggleSecondModalClose, reloadData,
}) => {
  const [paymentMethods, setPaymentMethods] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [amount, setAmount] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [agreeToFee, setAgreeToFee] = useState(false);
  const [loading, setLoading] = useState(false);
  const [elavonCardSession, setElavonCardSession] = useState(false);
  const [fundType, setFundType] = useState(FUND_TYPE_OPTIONS[0]);

  const [processingFee, setProcessingFee] = useState(0.05);
  const localCurrencySettings = { style: 'currency', currency: 'USD' };
  const handlerFeeAgreement = (e) => setAgreeToFee(e.target.checked);

  const handleElavonSuccess = () => {
    reloadData();
    closeModal();
  };
  const getCompanyProcessingFee = () => (
    axios.get('/api/companies/processing-fee')
      .then(res => setProcessingFee(res.data?.payload?.processingFee))
      .catch(error => {
        Notification('error', error);
        closeModal();
      })
  );


  const closeModal = () => {
    toggleModal(false, 'addFunds');
  };

  const getPaymentMethods = () => (
    axios.get('/api/payment/payment-methods')
      .then((res) => {
        setPaymentMethods(mapPaymentMethodsForSelect(res));
      })
      .catch((error) => {
        Notification('error', error);
        closeModal();
      })
  );

  const bankPaymentMethodOptions = paymentMethods?.filter((pm) => pm.type === 'bank') || [];
  const cardPaymentMethodOptions = paymentMethods?.filter((pm) => pm.type === 'card') || [];

  useEffect(() => {
    toggleModal(true, 'loader', true);
    getCompanyProcessingFee();
    getPaymentMethods().then(() => toggleSecondModalClose('loader', true));
  }, []);

  const openVerificationPopup = (url) => {
    const width = 500;
    const height = 700;
    const left = Math.round((window.screen.width - width) / 2);
    const top = Math.round((window.screen.height - height) / 2);
    window.open(
      url,
      'microdeposit_verification',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
    );
  };

  const handleAmount = (e) => {
    const result = fundsInputValueValidator(e.target.value);
    if (result.success) setAmount(result.value);
  };

  const handleBankSubmit = async () => {
    if (!isValidFundsAmount(amount)) {
      Notification('error', { message: 'Please enter an amount of at least $0.01' });
      return;
    }
    if (!agreeToFee) {
      Notification('error', { message: 'Please agree to the processing fees' });
      return;
    }
    if (!selectedCard?.paymentMethodId && !selectedCard?.value) {
      Notification('error', { message: 'Please select a saved bank account or enter new bank details' });
      return;
    }

    setLoading(true);
    try {
      const data = await chargeSavedBankAccount({
        paymentMethodId: selectedCard.paymentMethodId || selectedCard.value,
        amount: Number(amount),
      });

      const verificationUrl = data?.nextAction?.verify_with_microdeposits?.hosted_verification_url;
      if (verificationUrl) {
        openVerificationPopup(verificationUrl);
      }

      Notification('success', { message: data?.message || 'Funds added successfully!' });
      reloadData();
      closeModal();
    } catch (err) {
      Notification('error', {
        message: err.response?.data?.error || err.response?.data?.message || err.message,
      });
    }
    setLoading(false);
  };

  const handleCardSubmit = async () => {
    if (!isValidFundsAmount(amount)) {
      Notification('error', { message: 'Please enter an amount of at least $0.01' });
      return;
    }
    if (!agreeToFee) {
      Notification('error', { message: 'Please agree to the processing fees' });
      return;
    }
    setLoading(true);
    try {
      if (selectedCard?.provider === PROVIDER_ELAVON) {
        await chargeSavedElavonCard({
          paymentMethodId: selectedCard.value,
          hostedCardId: selectedCard.hostedCardId,
          amount: Number(totalAmount),
        });
      } else {
        const { data } = await axios.post('/api/payment/payment-transaction', {
          paymentMethod: selectedCard?.value,
          amount: Number(amount),
        });

        const verificationUrl = data?.nextAction?.verify_with_microdeposits?.hosted_verification_url;
        if (verificationUrl) {
          openVerificationPopup(verificationUrl);
        }
      }

      Notification('success', { message: 'Funds added successfully!' });
      reloadData();
      closeModal();
    } catch (err) {
      Notification('error', { message: err.response?.data?.error || err.message });
    }
    setLoading(false);
  };

  const platformFee = amount
    ? (grossAmount(Number(amount), processingFee).toFixed(2) - Number(amount))
    : 0;
  const cardFee = amount
    ? (grossAmount(Number(amount), 0.0329).toFixed(2) - Number(amount))
    : 0;
  const totalAmount = amount
    ? grossAmount(
      Number(amount),
      processingFee,
      fundType.value === 'card' ? 'card' : selectedCard?.type,
    ).toFixed(2)
    : 0;

  if (modalState.isOpen) {
    if (user) {
      return (
        <div className="popup_block dashboard-modal dashboard-modal--funds showed">
          <div className="popup_container">
            <div className="popup_head">
              <div className="popup_title">Add Funds</div>
              <button type="button" className="close_btn" aria-label="close" onClick={closeModal}>
                ×
              </button>
            </div>

            <div className="popup_body">
              <div className="field_col">
                <label className="field_name" htmlFor="fund-type-select">
                  Funding Type
                </label>
                <DashboardSelect
                  inputId="fund-type-select"
                  options={FUND_TYPE_OPTIONS}
                  value={fundType}
                  isSearchable={false}
                  onChange={(option) => {
                    setFundType(option || FUND_TYPE_OPTIONS[0]);
                    setSelectedCard(null);
                    setElavonCardSession(false);
                    setAgreeToTerms(false);
                    setAgreeToFee(false);
                  }}
                />
              </div>

              {fundType.value === 'bank' && (
                <>
                  <div className="field_col">
                    <label className="field_name" htmlFor="existing-payment-method">
                      Use Existing Payment Method
                    </label>
                    <DashboardSelect
                      inputId="existing-payment-method"
                      isClearable
                      options={bankPaymentMethodOptions}
                      value={selectedCard}
                      onChange={(e) => {
                        setSelectedCard(e ? e : null);
                        setAgreeToTerms(false);
                        setAgreeToFee(false);
                      }}
                    />
                  </div>

                  {!selectedCard && (
                    <>
                      <div className="separate">
                        <span>OR</span>
                      </div>

                      <AddBankAccount
                        email={user.email}
                        selectedCard={selectedCard?.value}
                        amount={amount}
                        onSuccess={() => {
                          reloadData();
                          closeModal();
                        }}
                        openVerificationPopup={openVerificationPopup}
                      />
                    </>
                  )}


                </>
              )}

              {fundType.value === 'card' && (
                <>
                  <div className="field_col">
                    <label className="field_name" htmlFor="existing-card-payment-method">
                      Use Existing Payment Method
                    </label>
                    <DashboardSelect
                      inputId="existing-card-payment-method"
                      isClearable
                      options={cardPaymentMethodOptions}
                      value={selectedCard}
                      onChange={(e) => {
                        setSelectedCard(e ? e : null);
                        setAgreeToTerms(false);
                        setAgreeToFee(false);
                        setElavonCardSession(false);
                      }}
                    />
                  </div>

                  {!selectedCard && (
                    <>
                      <div className="separate">
                        <span>OR</span>
                      </div>

                      <div className="field_col">
                        <span className="field_name">Pay with Credit Card (Checkout.js)</span>
                      </div>
                      <ConvergeCheckoutPayment
                        amount={amount}
                        agreeToFee={agreeToFee}
                        user={user}
                        onSuccess={handleElavonSuccess}
                      />
                    </>
                  )}
                </>
              )}

              <div className="field_col">
                <label className="field_name" htmlFor="amount">
                  Amount
                </label>
                <input
                  id="amount"
                  className="dashboard-input"
                  type="text"
                  inputMode="decimal"
                  placeholder="$ 0.00"
                  value={amount}
                  onChange={handleAmount}
                />
              </div>

              <p className="dashboard-funds-warning" style={{ color: 'yellow' }}>
                {fundType.value === 'card' && (
                  <>
                    <strong>Warning:</strong> Credit card funding may take up to 24 hours to be processed.
                  </>
                )}
                {fundType.value === 'bank' && (
                  <>
                    <strong>Warning:</strong> ACH funding may take up to 3 business days to be processed.
                  </>
                )}
              </p>

              <p className="dashboard-funds-info">
                To provide a secure and seamless environment for managing project funds, Wurqly assesses a flat platform service Fee of {processingFee * 100}% on all successful fund additions to your account. This fee is a platform usage charge that covers administrative costs, account maintenance, and the facilitation of secure payment processing.
              </p>


              <div className="dashboard-funds-summary">
                <div className="dashboard-funds-row">
                  <span className="dashboard-funds-label">
                    Platform Service Fee
                    {' '}
                    {processingFee * 100}
                    %
                    {' '}
                  </span>
                  <span className="dashboard-funds-value">
                    {platformFee.toLocaleString('en-US', localCurrencySettings)}
                  </span>
                </div>
                {(fundType.value === 'card' || selectedCard?.type === 'card') && (
                  <div className="dashboard-funds-row">
                    <span className="dashboard-funds-label">Credit Card Processing Fee {' '}3.29% {' '}</span>
                    <span className="dashboard-funds-value">
                      {cardFee.toLocaleString('en-US', localCurrencySettings)}
                    </span>
                  </div>
                )}
                <div className="dashboard-funds-row dashboard-funds-row--total">
                  <span className="dashboard-funds-label">Total:</span>
                  <span className="dashboard-funds-value">
                    {Number(totalAmount).toLocaleString('en-US', localCurrencySettings)}
                  </span>
                </div>
              </div>

              <label className="check_btn" htmlFor="agreeToFee">
                Click here to agree to processing fees
                <input
                  type="checkbox"
                  id="agreeToFee"
                  name="agreeToFee"
                  onChange={handlerFeeAgreement}
                  checked={agreeToFee}
                />
              </label>

              <div className="status_day">
                <span>FUNDS AVAILABILITY: 4-5 Business Days</span>
              </div>
            </div>

            <div className="popup_footer">
              <button
                type="button"
                className="dashboard-btn dashboard-btn--secondary"
                onClick={closeModal}
              >
                Cancel
              </button>
              {fundType.value === 'card' && selectedCard?.value ? (
                <button
                  type="button"
                  className="dashboard-btn dashboard-btn--primary"
                  disabled={loading || !isValidFundsAmount(amount) || !agreeToFee}
                  onClick={handleCardSubmit}
                >
                  {loading ? 'Processing...' : 'Add Funds'}
                </button>
              ) : fundType.value === 'bank' && !elavonCardSession && selectedCard?.value ? (
                <button
                  type="button"
                  className="dashboard-btn dashboard-btn--primary"
                  disabled={loading || !isValidFundsAmount(amount) || !agreeToFee}
                  onClick={handleBankSubmit}
                >
                  {loading ? 'Processing...' : 'Add Funds'}
                </button>
              ) : fundType.value === 'bank' && !elavonCardSession && !selectedCard?.value ? (
                <button
                  type="submit"
                  form="bankAccountForm"
                  className="dashboard-btn dashboard-btn--primary"
                  disabled={loading || !isValidFundsAmount(amount) || !agreeToFee}
                >
                  Add Funds
                </button>
              ) : null}
            </div>
          </div>
        </div>
      );
    }
    return <Loader />;
  }
  return null;
};

const mapDispatchToProps = {
  toggleModal,
  toggleSecondModalClose,
  reloadData,
};

const mapStateToProps = (state) => ({
  user: state.auth.user,
  modalState: state.modalState.addFunds,
});

export default connect(mapStateToProps, mapDispatchToProps)(AddFunds);
